(function () {

  /**
   * Initializes the countdown timer for a given section element.
   * @param {HTMLElement} sectionElement - The DOM element of the countdown timer section.
   */
  function initCountdownTimer(sectionElement) {
    if (!sectionElement) return;

    const sectionId = sectionElement.dataset.sectionId;
    const isPreviewMode = sectionElement.dataset.previewMode === 'true';

    const saleTextEl = sectionElement.querySelector('.countdown-timer__sale-text');
    const timerEl = sectionElement.querySelector('.countdown-timer__timer');
    const statusMessageEl = sectionElement.querySelector('.status-message');
    const closeButton = sectionElement.querySelector(`.countdown-timer__close-button`);

    // Labels with fallback defaults
    const daysLabel = sectionElement.dataset.daysLabel || 'Days';
    const hoursLabel = sectionElement.dataset.hoursLabel || 'Hrs';
    const minutesLabel = sectionElement.dataset.minutesLabel || 'Min';
    const secondsLabel = sectionElement.dataset.secondsLabel || 'Sec';

    // Apply labels across all duplicated visual elements
    sectionElement.querySelectorAll('.days-label').forEach(el => el.textContent = daysLabel);
    sectionElement.querySelectorAll('.hours-label').forEach(el => el.textContent = hoursLabel);
    sectionElement.querySelectorAll('.minutes-label').forEach(el => el.textContent = minutesLabel);
    sectionElement.querySelectorAll('.seconds-label').forEach(el => el.textContent = secondsLabel);
    sectionElement.querySelectorAll('.days-separator').forEach(el => el.textContent = ':');

    const BANNER_CLOSED_KEY = `countdownBannerClosed_${sectionId}`;
    let countdownInterval;

    // Utility: Validate date string format
    function isValidDateString(str) {
      return str && str.match(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?$/);
    }

    // Utility: Validate UTC offset format (e.g., +00:00 or -04:00)
    function isValidUtcOffset(offsetStr) {
      return offsetStr && offsetStr.match(/^[+-]\d{2}:\d{2}$/);
    }

    // Normalize date string to ensure seconds are present
    function normalizeDateString(str) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str + 'T00:00:00';
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(str)) return str + ':00';
      return str;
    }

    // --- MODIFICATION START ---
    // Hardcode the UTC offset for Central Time (US & Canada)
    const centralTimeOffset = '-07:00';

    // Parse and validate the sale start/end dates, explicitly using the Central Time offset
    const saleStartDate = new Date(normalizeDateString(sectionElement.dataset.saleStart) + centralTimeOffset);
    const saleEndDate = new Date(normalizeDateString(sectionElement.dataset.saleEnd) + centralTimeOffset);
    // --- MODIFICATION END ---

    if (isNaN(saleStartDate.getTime()) || isNaN(saleEndDate.getTime()) || saleEndDate <= saleStartDate) {
      handleValidationError('Invalid or out-of-order dates.');
      return;
    }

    // Handle errors in preview mode and log them to console
    function handleValidationError(message) {
      if (isPreviewMode && statusMessageEl) {
        statusMessageEl.textContent = message;
        statusMessageEl.style.display = 'block';
        sectionElement.classList.add('countdown-timer__section--visible');
      }
      console.error(`[Countdown Timer] ${message}`);
    }

    // Format number to two digits
    function formatValue(val) {
      return val < 10 ? `0${val}` : `${val}`;
    }

    // Update tens and ones for each time unit
    function updateDigits(className, value) {
      const [tens, ones] = formatValue(value).split('');
      sectionElement.querySelectorAll(`.${className}-tens`).forEach(el => el.textContent = tens);
      sectionElement.querySelectorAll(`.${className}-ones`).forEach(el => el.textContent = ones);
    }

    // Render all time segments (days, hours, minutes, seconds)
    function renderTime(ms, isUpcoming) {
      const totalSeconds = Math.floor(ms / 1000);
      const s = totalSeconds % 60;
      const m = Math.floor(totalSeconds / 60) % 60;
      const h = Math.floor(totalSeconds / 3600) % 24;
      const d = Math.floor(totalSeconds / 86400);

      // Determine visibility for each time segment
      const showDays = d > 0 || isUpcoming || Math.floor(totalSeconds / 3600) >= 24;
      const showHours = h > 0 || showDays || isUpcoming;
      const showMinutes = m > 0 || showHours || isUpcoming;
      const showSeconds = s > 0 || showMinutes || isUpcoming;

      // Days
      sectionElement.querySelectorAll('.days-container').forEach(container => {
        container.classList.toggle('countdown-timer__time-segment--hidden', !showDays);
      });
      sectionElement.querySelectorAll('.days-separator').forEach(separator => {
        separator.classList.toggle('countdown-timer__separator--hidden', !showDays);
      });

      // Hours
      sectionElement.querySelectorAll('.hours-container').forEach(container => {
        container.classList.toggle('countdown-timer__time-segment--hidden', !showHours);
      });

      // Minutes
      sectionElement.querySelectorAll('.minutes-container').forEach(container => {
        container.classList.toggle('countdown-timer__time-segment--hidden', !showMinutes);
      });

      // Seconds
      sectionElement.querySelectorAll('.seconds-container').forEach(container => {
        container.classList.toggle('countdown-timer__time-segment--hidden', !showSeconds);
      });

      // Update digits
      if (showDays) updateDigits('days', d);
      if (showHours) updateDigits('hours', h);
      if (showMinutes) updateDigits('minutes', m);
      if (showSeconds) updateDigits('seconds', s);
    }


    /**
     * Main function that runs every second to update the timer state
     */
    function updateCountdown() {
      const now = new Date();
      const timeUntilStart = saleStartDate - now;
      const timeUntilEnd = saleEndDate - now;

      if (sessionStorage.getItem(BANNER_CLOSED_KEY) === 'true' && !isPreviewMode) {
        hideSection();
        return;
      }

      if (timeUntilStart > 0) {
        hideSection();
        if (isPreviewMode) {
          showPreviewMessage(`Sale starts in ${formatRemainingTime(timeUntilStart)}.`, true);
        }
        return;
      }

      if (timeUntilEnd <= 0) {
        clearInterval(countdownInterval);
        if (sectionElement.dataset.hideAfterSaleEnds === 'true') {
          hideSection();
        } else {
          showEndedMessage(sectionElement.dataset.saleEndedMessage || 'Sale has ended.');
        }
        return;
      }

      showSection();
      renderTime(timeUntilEnd, false);
    }

    // Show/hide section helpers
    function showSection() {
      sectionElement.classList.add('countdown-timer__section--visible');
    }

    function hideSection() {
      sectionElement.classList.remove('countdown-timer__section--visible');
    }

    function showPreviewMessage(msg, showTimer) {
      if (statusMessageEl) {
        statusMessageEl.textContent = msg;
        statusMessageEl.style.display = 'block';
      }
      showSection();
      if (showTimer) renderTime(saleStartDate - new Date(), true);
    }

    function showEndedMessage(msg) {
      if (statusMessageEl) {
        statusMessageEl.textContent = msg;
        statusMessageEl.style.display = 'block';
        saleTextEl.style.display = 'none';
        timerEl.style.display = 'none';
      }
      showSection();
    }

    // Format remaining time as a readable string
    function formatRemainingTime(ms) {
      const totalSeconds = Math.floor(ms / 1000);
      const d = Math.floor(totalSeconds / 86400);
      const h = Math.floor((totalSeconds % 86400) / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      return `${d}d ${h}h ${m}m ${s}s`;
    }

    // Handle close button functionality (if enabled)
    if (sectionElement.dataset.enableCloseButton === 'true' && closeButton) {
      closeButton.addEventListener('click', function () {
        sessionStorage.setItem(BANNER_CLOSED_KEY, 'true');
        hideSection();
        clearInterval(countdownInterval);
      });
    }

    // Initial update and begin interval loop
    updateCountdown();
    countdownInterval = setInterval(updateCountdown, 1000);
    sectionElement._countdownInterval = countdownInterval;
  }

  /**
   * Prepares a countdown timer section by clearing old intervals and reinitializing it.
   * @param {string} sectionId - ID of the section
   */
  function setupSection(sectionId) {
    const sectionElement = document.querySelector(`.countdown-timer__section[data-section-id="${sectionId}"]`);
    if (!sectionElement) return;

    if (sectionElement._countdownInterval) {
      clearInterval(sectionElement._countdownInterval);
      delete sectionElement._countdownInterval;
    }

    initCountdownTimer(sectionElement);
  }

  // Initialize all countdown sections on page load
  document.querySelectorAll('.countdown-timer__section').forEach(sectionElement => {
    const sectionId = sectionElement.dataset.sectionId;
    setupSection(sectionId);
  });

  // Support dynamic behavior inside Shopify's Theme Editor
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const el = document.querySelector(`.countdown-timer__section[data-section-id="${e.detail.sectionId}"]`);
      if (el) setupSection(e.detail.sectionId);
    });

    document.addEventListener('shopify:section:unload', (e) => {
      const el = document.querySelector(`.countdown-timer__section[data-section-id="${e.detail.sectionId}"]`);
      if (el && el._countdownInterval) {
        clearInterval(el._countdownInterval);
        delete el._countdownInterval;
      }
    });
  }
})();
